/**
 * SmartDocMode - Smart document view embedded in workspace
 * Integrates EntityAwareEditor with document management
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  FileText,
  Plus,
  Trash2,
  Sparkles,
  Calendar,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDocumentStore } from '@/stores/documentStore'
import { EntityAwareEditor } from '@/components/smart-docs/EntityAwareEditor'
import { EntitySidebar } from '@/components/smart-docs/EntitySidebar'
import type { SmartDocument } from '@/types/document'

interface SmartDocModeProps {
  context: unknown // Workspace context (unused - documents have own state)
}

export function SmartDocMode({ context: _context }: SmartDocModeProps) {
  const [entitySidebarCollapsed, setEntitySidebarCollapsed] = useState(false)

  const documents = useDocumentStore((state) => state.documents)
  const currentDocumentId = useDocumentStore((state) => state.currentDocumentId)
  const createDocument = useDocumentStore((state) => state.createDocument)
  const setCurrentDocument = useDocumentStore((state) => state.setCurrentDocument)
  const deleteDocument = useDocumentStore((state) => state.deleteDocument)
  const updateDocument = useDocumentStore((state) => state.updateDocument)

  const currentDocument = documents.find((d) => d.id === currentDocumentId)

  // Create initial document if none exists
  useEffect(() => {
    if (documents.length === 0) {
      createDocument('Product Discovery Notes')
    } else if (!currentDocumentId && documents.length > 0) {
      setCurrentDocument(documents[0].id)
    }
  }, [documents, currentDocumentId, createDocument, setCurrentDocument])

  const handleCreateDocument = () => {
    const doc = createDocument('Untitled Document')
    setCurrentDocument(doc.id)
  }

  const handleDeleteDocument = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('Delete this document?')) {
      deleteDocument(id)
    }
  }

  const handleTitleChange = (id: string, title: string) => {
    updateDocument(id, { title })
  }

  return (
    <div className="h-full flex">
      {/* Document List Sidebar */}
      <div
        className="w-64 border-r border-border flex flex-col"
        style={{ backgroundColor: 'var(--color-warm-100)' }}
      >
        <div className="p-3 border-b border-border">
          <h3 className="font-medium text-sm mb-2">Documents</h3>
          <Button
            onClick={handleCreateDocument}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-3.5 h-3.5" />
            New Document
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {documents.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No documents yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {documents.map((doc) => (
                <DocItem
                  key={doc.id}
                  document={doc}
                  isActive={doc.id === currentDocumentId}
                  onClick={() => setCurrentDocument(doc.id)}
                  onDelete={(e) => handleDeleteDocument(doc.id, e)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {currentDocument ? (
          <>
            {/* Document Header */}
            <div className="p-4 border-b border-border bg-white">
              <div className="flex items-center gap-3 mb-1">
                <FileText size={20} style={{ color: 'var(--color-market)' }} />
                <input
                  type="text"
                  value={currentDocument.title}
                  onChange={(e) => handleTitleChange(currentDocument.id, e.target.value)}
                  className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-0 flex-1"
                  placeholder="Document title..."
                />
              </div>
              <div className="flex items-center gap-3 text-xs text-text-muted ml-8">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(currentDocument.updatedAt).toLocaleDateString()}
                </span>
                {currentDocument.detectedEntities.filter((e) => e.status === 'pending').length > 0 && (
                  <Badge variant="secondary" className="text-[10px]">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {currentDocument.detectedEntities.filter((e) => e.status === 'pending').length} pending
                  </Badge>
                )}
              </div>
            </div>

            {/* Editor + Entity Sidebar */}
            <div className="flex-1 flex min-h-0 relative">
              {/* Editor */}
              <div className="flex-1 min-w-0">
                <EntityAwareEditor documentId={currentDocument.id} />
              </div>

              {/* Entity Sidebar */}
              {!entitySidebarCollapsed && (
                <div className="w-72 border-l border-border">
                  <EntitySidebar documentId={currentDocument.id} />
                </div>
              )}

              {/* Sidebar Toggle */}
              <button
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1 bg-warm-100 border border-border rounded-l-md hover:bg-warm-200 transition-colors z-10"
                onClick={() => setEntitySidebarCollapsed(!entitySidebarCollapsed)}
                title={entitySidebarCollapsed ? 'Show entities' : 'Hide entities'}
                style={{ right: entitySidebarCollapsed ? 0 : 'calc(18rem - 1px)' }}
              >
                <ChevronRight
                  className={cn(
                    'w-4 h-4 transition-transform',
                    !entitySidebarCollapsed && 'rotate-180'
                  )}
                />
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-text-muted">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg mb-2">No document selected</p>
              <Button onClick={handleCreateDocument} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Document
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface DocItemProps {
  document: SmartDocument
  isActive: boolean
  onClick: () => void
  onDelete: (e: React.MouseEvent) => void
}

function DocItem({ document, isActive, onClick, onDelete }: DocItemProps) {
  const pendingCount = document.detectedEntities.filter(
    (e) => e.status === 'pending'
  ).length

  return (
    <div
      className={cn(
        'group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors',
        isActive
          ? 'bg-white shadow-sm border border-border'
          : 'hover:bg-warm-200'
      )}
      onClick={onClick}
    >
      <FileText className="w-4 h-4 text-text-muted flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{document.title}</p>
        <p className="text-[10px] text-text-muted">
          {new Date(document.updatedAt).toLocaleDateString()}
        </p>
      </div>
      {pendingCount > 0 && (
        <Badge variant="secondary" className="text-[10px] px-1.5">
          {pendingCount}
        </Badge>
      )}
      <button
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-warm-200 transition-all"
        onClick={onDelete}
        title="Delete document"
      >
        <Trash2 className="w-3 h-3 text-text-muted" />
      </button>
    </div>
  )
}
